import React from 'react';
import { T } from '../customer/customers.theme';
import { EditIcon, Spinner, TrashIcon } from '../customer/CustomersUI';
import { PrinterIcon } from '../menu/MenuUI';

/**
 * The station printers.
 *
 * The phone's row, brought across: name, the address underneath, an OFF badge
 * when it is switched off, and three actions. Only the row being worked on
 * shows a spinner — the rest of the list stays usable, which matters when a
 * manager is testing four printers one after another.
 */

const COPY = {
  test: 'Test print',
  off: 'OFF',
  emptyTitle: 'No printers yet',
  emptyNote: 'Add a printer, then set which station each category prints to.',
};

function RowAction({ label, tone, onClick, children }) {
  const [hovered, setHovered] = React.useState(false);
  const danger = tone === 'danger';

  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 30,
        height: 30,
        background: hovered ? (danger ? T.dangerTint : T.hover) : 'transparent',
        border: 'none',
        borderRadius: T.radiusControl,
        color: hovered ? (danger ? T.danger : T.ink) : T.inkGhost,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

export default function PrinterList({ printers, busyId, onTest, onEdit, onDelete }) {
  if (!printers.length) {
    return (
      <div style={{ padding: '10px 2px', fontSize: 12.5, color: T.inkFaint, lineHeight: 1.6 }}>
        {COPY.emptyTitle}. {COPY.emptyNote}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {printers.map((printer) => {
        const active = printer.isActive !== false;
        const busy = busyId === printer._id;

        return (
          <div
            key={printer._id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              padding: '13px 18px',
              background: T.surface,
              border: `1px solid ${T.border}`,
              borderRadius: T.radiusPanel,
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 34,
                height: 34,
                background: active ? T.selectedBg : T.shade,
                borderRadius: T.radiusControl,
                color: active ? T.brand : T.inkGhost,
                flexShrink: 0,
              }}
            >
              <PrinterIcon size={17} />
            </span>

            <div
              style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <span
                  title={printer.name}
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: active ? T.ink : T.inkFaint,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {printer.name}
                </span>
                {active ? null : (
                  <span
                    style={{
                      flexShrink: 0,
                      padding: '2px 8px',
                      background: T.shade,
                      border: `1px solid ${T.border}`,
                      borderRadius: T.radiusPill,
                      fontSize: 10,
                      fontWeight: 700,
                      color: T.inkFaint,
                      letterSpacing: 0.4,
                    }}
                  >
                    {COPY.off}
                  </span>
                )}
              </span>
              <span style={{ fontSize: 12, color: T.inkFaint, fontVariantNumeric: 'tabular-nums' }}>
                {printer.host}:{printer.port || 9100}
              </span>
            </div>

            {busy ? (
              <span style={{ display: 'flex', color: T.inkFaint, paddingRight: 24 }}>
                <Spinner size={16} />
              </span>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button
                  type="button"
                  onClick={() => onTest(printer)}
                  style={{
                    height: 30,
                    padding: '0 12px',
                    background: T.surface,
                    border: `1px solid ${T.borderStrong}`,
                    borderRadius: T.radiusControl,
                    font: 'inherit',
                    fontSize: 12,
                    fontWeight: 600,
                    color: T.inkSoft,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {COPY.test}
                </button>
                <RowAction label="Edit" onClick={() => onEdit(printer)}>
                  <EditIcon size={15} />
                </RowAction>
                <RowAction label="Delete" tone="danger" onClick={() => onDelete(printer)}>
                  <TrashIcon size={15} />
                </RowAction>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
